import { Component, inject, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, EMPTY, of } from 'rxjs';
import {
  distinctUntilChanged,
  first,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { getAccrualPeriodTypes } from 'src/app/product-capture/model/consts/accrual-type.const';
import { ACCRUAL_TYPE_ENUM } from 'src/app/product-capture/model/enums/accrual-type.enum';
import { COUPON_TYPE } from 'src/app/product-capture/model/enums/coupon-type.enum';
import {
  CouponBarrierEnum,
  CouponEnum,
  KeyDatesEnum,
  PayoffEnum,
  ScheduleEnum,
  ScheduleType,
} from 'src/app/product-capture/model/enums/product-capture.enums';
import { RATE_RESET_ENUM } from 'src/app/product-capture/model/enums/rate-reset.enum';
import { SCHEDULE_TYPE_ENUM } from 'src/app/product-capture/model/enums/schedule-type.enum';
import { CommonProductCaptureDataService } from '../../../common-product-capture-data.service';
import {
  CouponSchedulePayload,
  ScheduleStrategyKeys,
} from '../../../event-schedules/event-schedules.const';
import { ScheduleComponent } from '../schedule.component';

@Component({
  selector: 'app-coupon-schedule',
  templateUrl: '../schedule.component.html',
  styleUrls: ['./coupon-schedule.component.scss', '../schedule.component.scss'],
  providers: [
    { provide: ScheduleComponent, useExisting: CouponScheduleComponent },
  ],
})
export class CouponScheduleComponent extends ScheduleComponent {
  key = ScheduleType.COUPON;

  schedulePayoffKey = PayoffEnum.coupon;

  featureKey = CouponEnum.couponType;

  private readonly COUPON_TYPES_TO_SHOW_REF_RATE_FIXING_LAG: string[] = [
    COUPON_TYPE.FIXED_TO_FLOATING,
    COUPON_TYPE.FLOATING,
    COUPON_TYPE.FLOATING_TO_FIXED,
  ];

  private commonProductCaptureDataService = inject(
    CommonProductCaptureDataService
  );

  allowedScheduleStrategies: string[] = [
    ScheduleStrategyKeys.CALL_FROM_COUPON,
    ScheduleStrategyKeys.INDEPENDENT_COUPON_AND_CALL,
    ScheduleStrategyKeys.COUPON_ONLY,
  ];

  ngOnInit(): void {
    super.ngOnInit();
    this.setAccrualLagBaseScheduleType();
    this.fetchReferenceOptions();
    this.setupReferenceRateFormChanges();
    this.onScheduleTypeChange();
  }

  applyDefaultScheduleType(): void {
    if (!this.scheduleStrategy) {
      this.scheduleType.setValue(null);
      return;
    }

    const isAdvancedCouponProduct = this.isAdvancedCouponProduct();
    const forbiddenScheduleTypes = isAdvancedCouponProduct
      ? [SCHEDULE_TYPE_ENUM.PAYMENT_ONLY]
      : [SCHEDULE_TYPE_ENUM.OBS_PRIMARY, SCHEDULE_TYPE_ENUM.INDEPENDENT];

    this.scheduleTypeItems = this.scheduleTypeItems.filter(
      (item) => !forbiddenScheduleTypes.includes(item.id)
    );

    if (this.isEditing) {
      return;
    }

    const isCouponStrategy = this.allowedScheduleStrategies.includes(
      this.scheduleStrategy
    );

    if (!isCouponStrategy) {
      return;
    }

    this.scheduleType.setValue(
      isAdvancedCouponProduct
        ? SCHEDULE_TYPE_ENUM.OBS_PRIMARY
        : SCHEDULE_TYPE_ENUM.PAYMENT_ONLY,
      { emitEvent: false }
    );
    this.setDefaultAccrualScheduleLagBase();
  }

  isAdvancedCouponProduct(): boolean {
    return this.ADVANCED_COUPON_PRODUCTS.includes(this.getCouponType());
  }

  setAccrualLagBaseScheduleType(): void {
    this.scheduleType.valueChanges
      .pipe(startWith(this.scheduleTypeValue), takeUntil(this.destroy$))
      .subscribe(() => this.setDefaultAccrualScheduleLagBase());
  }

  setDefaultAccrualScheduleLagBase(): void {
    if (!this.scheduleTypeValue) {
      this.accrualScheduleLagBase.setValue(null);
      return;
    }

    this.accrualScheduleLagBaseTypes = getAccrualPeriodTypes(
      this.getCouponType(),
      this.scheduleTypeValue
    );

    const accrualScheduleLagBaseControl = this.scheduleForm.get(
      ScheduleEnum.accrualScheduleLagBase
    );

    const isValidOption = this.accrualScheduleLagBaseTypes.find(
      (option) => option.id === accrualScheduleLagBaseControl.value
    );
    if (!isValidOption) {
      accrualScheduleLagBaseControl.setValue(null);
      accrualScheduleLagBaseControl.markAsUntouched();
    }

    // Should we avoid defaulting if the control is touched?
    if (
      accrualScheduleLagBaseControl.touched ||
      !!accrualScheduleLagBaseControl.value
    ) {
      return;
    }

    if (this.scheduleTypeValue === SCHEDULE_TYPE_ENUM.OBS_PRIMARY) {
      accrualScheduleLagBaseControl.setValue(ACCRUAL_TYPE_ENUM.OBS_UNADJUSTED);
    } else if (
      [
        SCHEDULE_TYPE_ENUM.PAYMENT_ONLY,
        SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY,
        SCHEDULE_TYPE_ENUM.INDEPENDENT,
      ].includes(this.scheduleTypeValue)
    ) {
      accrualScheduleLagBaseControl.setValue(
        ACCRUAL_TYPE_ENUM.PAYMENT_UNADJUSTED
      );
    } else if (this.scheduleTypeValue === SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY) {
      accrualScheduleLagBaseControl.setValue(ACCRUAL_TYPE_ENUM.UNADJUSTED);
    }
  }

  fetchReferenceOptions(): void {
    this.commonProductCaptureDataService.referenceRateOptions$
      .pipe(first())
      .subscribe((productReferenceRates) => {
        this.referenceRateOptions = productReferenceRates;
      });
  }

  getColumnsByScheduleType(): {} {
    return {
      barrierLevel: this.getCouponType() === COUPON_TYPE.CONDITIONAL,
      rateDeterminationDate:
        this.isFloatingCoupon() && this.key === ScheduleType.COUPON,
      unadjObsDate:
        this.ADVANCED_COUPON_PRODUCTS.includes(this.getCouponType()) &&
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.PAYMENT_ONLY &&
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.PAYMENT_PRIMARY,
      adjObsDate:
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.PAYMENT_ONLY &&
        this.ADVANCED_COUPON_PRODUCTS.includes(this.getCouponType()),
      accrualStartDate: true,
      accrualEndDate: true,
      unadjPmtDate:
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.OBS_PRIMARY &&
        this.scheduleTypeValue !== SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY,
      rangeAccrualRate:
        this.getCouponType() === COUPON_TYPE.RANGE_ACCRUAL ||
        this.getCouponType() === COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL,
      lowerLevel:
        this.getCouponType() === COUPON_TYPE.RANGE_ACCRUAL ||
        this.getCouponType() === COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL,
      upperLevel:
        this.getCouponType() === COUPON_TYPE.RANGE_ACCRUAL ||
        this.getCouponType() === COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL,
    };
  }

  private setupReferenceRateFormChanges(): void {
    const referenceRateControl = this.payoffForm
      .get(PayoffEnum.coupon)
      ?.get(CouponEnum.referenceRate);

    combineLatest([
      referenceRateControl.valueChanges.pipe(
        startWith(referenceRateControl.value)
      ),
      this.commonProductCaptureDataService.referenceRateOptions$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([referenceRateValue, referenceRateOptions]) => {
        if (referenceRateValue && referenceRateOptions?.length) {
          this.updateReferenceRateLagLabel(
            referenceRateValue,
            referenceRateOptions
          );

          return;
        }
      });
  }

  onScheduleTypeChange(): void {
    this.scheduleType.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(this.scheduleTypeValue),
        distinctUntilChanged(),
        switchMap((value) => {
          if (value !== SCHEDULE_TYPE_ENUM.ACCRUAL_PRIMARY) {
            return EMPTY;
          }

          return of(value);
        })
      )
      .subscribe(() => this.scheduleTypeChanged.emit());
  }

  private updateReferenceRateLagLabel(
    referenceRateValue: string,
    referenceRateOptions: any[]
  ): void {
    const referenceRateId = referenceRateValue?.split('|')?.[0]?.trim();
    const referenceRate = referenceRateOptions.find(
      (option) => option.assetId === referenceRateId
    );

    const refRateFixingLagControl = this.scheduleForm.get(
      ScheduleEnum.refRateFixingLag
    );

    const settlementLag = referenceRate?.settlementLag;
    const publishingLag = referenceRate?.publishingLag;

    this.referenceRateLagLabel = publishingLag
      ? 'Ref. Rate Publishing Lag'
      : 'Ref. Rate Settlement Lag';

    if (
      refRateFixingLagControl.value ||
      this.product?.id ||
      refRateFixingLagControl.touched
    ) {
      return;
    }

    refRateFixingLagControl.setValue(settlementLag ?? publishingLag, {
      emitEvent: false,
    });
  }

  // Fields visibility
  override setFieldsVisibility(): void {
    this.fieldsVisiblity = {
      scheduleType: this.getScheduleTypeVisibility(),
      pmtScheduleLag: this.getComputedPaymentLagVisibility(),
      refRateFixingLag: this.getReferenceRateFixingLagVisibility(),
      accrualScheduleLagBase: signal(true),
      callNoticeLag: signal(false),
      callNoticeLagBaseType: signal(false),
      lastPeriodCallable: signal(false),
      referenceRate: this.observeFieldVisibility(CouponEnum.referenceRate),
      rateReset: this.observeFieldVisibility(CouponEnum.rateReset),
      fixedPeriods: this.observeFieldVisibility(CouponEnum.fixedPeriods),
      floatingPeriods: this.observeFieldVisibility(CouponEnum.floatingPeriods),
    };
  }

  getSchedulePayload(): CouponSchedulePayload {
    const referenceRate =
      this.coupon?.get(CouponEnum.referenceRate)?.value || '';

    const underlyingAssetReferenceRate =
      this.findUnderlyingAssetReferenceRate(referenceRate);
    const referenceRateLagKey = underlyingAssetReferenceRate?.publishingLag
      ? 'publishingLag'
      : 'settlementLag';

    const { schedule, obsRollConfig, ...payload } = this.getScheduleFormValue();

    let coupon: CouponSchedulePayload = {
      ...payload,
      type: this.getCouponType(),
      frequency: this.getCouponValue(CouponEnum.frequency),
      dayCount: this.getCouponValue(CouponEnum.dayCount),
      pmtRollConvention: this.getRollConvention(
        KeyDatesEnum.settlementRollConvention
      ),
      pmtScheduleLag: payload.pmtScheduleLag || '0B',
      pmtScheduleLagBase:
        payload.pmtScheduleLagBase || ACCRUAL_TYPE_ENUM.UNADJUSTED,
      accrualRollConvention: this.getRollConvention(
        KeyDatesEnum.accrualRollConvention
      ),
      referenceRate: {
        assetId: underlyingAssetReferenceRate?.assetId || '',
        calendarCodes: underlyingAssetReferenceRate?.calendars || [],
        [referenceRateLagKey]:
          this.refRateFixingLag.value ??
          (underlyingAssetReferenceRate?.settlementLag || ''),
      },
      rateReset: this.getCouponValue(CouponEnum.rateReset),
      fixedPeriods: this.getCouponValue(CouponEnum.fixedPeriods),
      floatingPeriods: this.getCouponValue(CouponEnum.floatingPeriods),
    };

    if (this.getCouponValue(CouponEnum.rateReset) === RATE_RESET_ENUM.DAILY) {
      coupon = {
        ...coupon,
        rateReset: RATE_RESET_ENUM.DAILY,
        rateCalculationMethod: this.coupon.get(CouponEnum.rateCalculationMethod)
          ?.value,
        rateResetSuspensionLength: this.coupon.get(
          CouponEnum.rateResetSuspensionLength
        )?.value,
        rateResetLookbackShift: this.coupon.get(
          CouponEnum.rateResetLookbackShift
        )?.value,
        rateObservationPeriodShift: this.coupon.get(
          CouponEnum.rateObservationPeriodShift
        )?.value,
        rateObsPeriodShiftExtraCalendars:
          this.coupon.get(CouponEnum.rateObsPeriodShiftExtraCalendars)?.value ??
          [],
        rateCalculationPeriodEndType: 'ACCRUAL_PERIOD',
      };
    }

    if (this.isConditionalCoupon()) {
      const obsFrequency = this.featureForm
        .get(CouponEnum.barrier)
        ?.get(CouponBarrierEnum.obsFrequency)?.value;

      coupon.barrier = {
        obsScheduleLag: '0B',
        obsScheduleLagBase: 'PAYMENT_ADJUSTED',
        obsRollConvention: this.getRollConvention(
          KeyDatesEnum.tradingRollConvention
        ),
        obsFrequency,
        fixFrequency: 'INITIAL_FIXING_DATE',
        ...(obsRollConfig && { obsRollConfig }),
      };
    }

    if (this.hasPerformance()) {
      coupon.performance = {
        obsScheduleLag: '0B',
        obsScheduleLagBase: 'PAYMENT_ADJUSTED',
        obsRollConvention: this.getRollConvention(
          KeyDatesEnum.tradingRollConvention
        ),
        obsFrequency: 'PERIOD_END',
        fixFrequency: 'PERIOD_START',
        ...(obsRollConfig && { obsRollConfig }),
      };
    }

    if (this.scheduleStrategy === ScheduleStrategyKeys.COUPON_FROM_CALL) {
      const { pmtScheduleLag, pmtScheduleLagBase, scheduleType } =
        this.callScheduleForm.getRawValue();
      coupon = {
        ...coupon,
        pmtScheduleLag,
        pmtScheduleLagBase,
        scheduleType,
      };
    }

    return coupon;
  }

  hasPerformance(): boolean {
    const { [this.featureKey]: type } = this.featureForm.value;

    const performanceTypes = [
      COUPON_TYPE.CALL,
      COUPON_TYPE.FORWARD,
      COUPON_TYPE.DIGITAL,
      COUPON_TYPE.FIXED_TO_CALL,
      COUPON_TYPE.FIXED_TO_FORWARD,
      COUPON_TYPE.FIXED_TO_DIGITAL,
      COUPON_TYPE.LOWEST_FX_PERFORMANCE,
    ];

    return performanceTypes.includes(type);
  }

  isConditionalCoupon(): boolean {
    const type = this.getCouponType();

    return (
      COUPON_TYPE.CONDITIONAL === type ||
      COUPON_TYPE.SNOWBALL === type ||
      COUPON_TYPE.RANGE_ACCRUAL === type ||
      COUPON_TYPE.FIXED_TO_RANGE_ACCRUAL === type
    );
  }

  getReferenceRateFixingLagVisibility(): Signal<boolean> {
    const couponType = this.coupon.get(CouponEnum.couponType);

    return this.showField(couponType, (couponType: CouponEnum) =>
      this.COUPON_TYPES_TO_SHOW_REF_RATE_FIXING_LAG.includes(couponType)
    );
  }

  observeFieldVisibility(fieldName: string): Signal<boolean> {
    return toSignal(
      this.featureForm.get(fieldName).statusChanges.pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        startWith(this.featureForm.get(fieldName).status),
        map((status: string) => status === 'VALID')
      ),
      { injector: this.injector }
    );
  }
}
